import { ReactNode } from 'react';

import { ActionsChatInput } from '@ui-furniture/common/chat-ui/inputs/actions';
import { AddressChatInput } from '@ui-furniture/common/chat-ui/inputs/address';
import { ButtonChatInput } from '@ui-furniture/common/chat-ui/inputs/button';
import {
  DateChatInput,
  DateChatInputConfig,
} from '@ui-furniture/common/chat-ui/inputs/date';
import {
  SingleSelectChatInput,
  SingleSelectChatInputConfig,
} from '@ui-furniture/common/chat-ui/inputs/single-select';
import {
  TextChatInput,
  TextChatInputConfig,
} from '@ui-furniture/common/chat-ui/inputs/text';
import { currencyFormatter } from '@ui-furniture/lib/utils/data-transform/currency-formatter';
import { numberParser } from '@ui-furniture/lib/utils/data-transform/number-parser';

interface Config {
  setChatInput: (input: ReactNode) => void;
  showUserResponse: (text: string) => void;
}

export class WebChatUserInputTools {
  private readonly _setChatInput: Config['setChatInput'];
  private readonly _showUserResponse: Config['showUserResponse'];

  constructor({ setChatInput, showUserResponse }: Config) {
    this._setChatInput = setChatInput;
    this._showUserResponse = showUserResponse;
  }

  private _sendTextInput<TAnswer extends { text: string }>({
    config,
    formatAnswer,
  }: {
    config: TextChatInputConfig;
    formatAnswer: (response: string) => TAnswer;
  }) {
    return new Promise<TAnswer>((resolve) => {
      this._setChatInput(
        <TextChatInput
          config={config}
          onAnswer={(value) => {
            const answer = formatAnswer(value);
            this._showUserResponse(answer.text);
            resolve(answer);
          }}
        />
      );
    });
  }

  getTextInput =
    (config: TextChatInputConfig = {}) =>
    () =>
      this._sendTextInput({
        config,
        formatAnswer: (value) => ({ text: value }),
      });

  getIntegerInput = () => () =>
    this._sendTextInput({
      config: {
        inputType: 'number',
      },
      formatAnswer: (valueText) => {
        const value = numberParser.roundInteger(valueText);
        return {
          text: valueText,
          value,
        };
      },
    });

  getCurrencyInput = (config?: { defaultValue?: string }) => () =>
    this._sendTextInput({
      config: {
        prefix: '£',
        inputType: 'number',
        defaultValue: config?.defaultValue,
      },
      formatAnswer: (valueText) => {
        const value = numberParser.roundInteger(valueText);
        const message = currencyFormatter.format(value);
        return {
          text: message,
          value,
        };
      },
    });

  getSingleSelectInput =
    <TValue,>(config: SingleSelectChatInputConfig<TValue>) =>
    () =>
      new Promise<{ label: string; value: TValue }>((resolve) => {
        this._setChatInput(
          <SingleSelectChatInput
            config={config}
            onAnswer={(selectedOption) => {
              this._showUserResponse(selectedOption.label);
              resolve(selectedOption);
            }}
          />
        );
      });

  getBoolInput = ({
    confirmLabel = 'Yes',
    denyLabel = 'No',
  }: {
    confirmLabel?: string;
    denyLabel?: string;
  } = {}) =>
    this.getSingleSelectInput({
      options: [
        { label: confirmLabel, value: true },
        { label: denyLabel, value: false },
      ],
    });

  getAddressInput =
    <TAnswer extends { id: string; address: string }>() =>
    () =>
      new Promise<TAnswer>((resolve) => {
        this._setChatInput(
          <AddressChatInput
            onAnswer={({ text, id }) => {
              this._showUserResponse(text);
              resolve({ id, address: text } as TAnswer);
            }}
          />
        );
      });

  getDateInput =
    <TAnswer extends { value: string }>(config: DateChatInputConfig) =>
    () =>
      new Promise<TAnswer>((resolve) => {
        this._setChatInput(
          <DateChatInput
            onAnswer={(date) => {
              this._showUserResponse(date);
              resolve({ value: date } as TAnswer);
            }}
            config={config}
          />
        );
      });

  getNoInput = () => () => Promise.resolve<void>(undefined);

  getActionInput =
    (config: {
      primary: {
        label: string;
        userResponseText?: string;
      };
      secondary?: {
        label: string;
        userResponseText?: string;
      };
    }) =>
    () =>
      new Promise<'primary' | 'secondary'>((resolve) => {
        this._setChatInput(
          <ActionsChatInput
            config={{
              primaryLabel: config.primary.label,
              secondaryLabel: config.secondary?.label,
            }}
            onAnswer={(selectedOption) => {
              const { userResponseText } = config[selectedOption] || {};
              if (userResponseText) this._showUserResponse(userResponseText);
              resolve(selectedOption);
            }}
          />
        );
      });

  getConfirmationInput = (config: {
    label: string;
    userResponseText?: string;
  }) =>
    this.getActionInput({
      primary: config,
    });

  getButtonInput =
    ({
      label,
      submit,
      userResponseText,
    }: {
      label: string;
      submit: () => Promise<boolean>;
      userResponseText?: string;
    }) =>
    () =>
      new Promise<void>((resolve) => {
        this._setChatInput(
          <ButtonChatInput
            config={{
              label,
              submit,
            }}
            onAnswer={async () => {
              if (userResponseText) this._showUserResponse(userResponseText);
              resolve();
            }}
          />
        );
      });
}
